import { useEffect, useState, useCallback } from "react";
import Layout from "../Shared/Layout";
import "../../ComponentCss/myorder.css";
import { FaChevronDown } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import TrackOrder from "./TrackOrder";
import toast from "react-hot-toast";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import ViewOrderDetail from "./ViewOrderDetail";
import { API_URL } from "../../_services/apiUrl";
import { get, post } from "../../_services/apiService";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import Breadcrumb from "../Breadcrumb";
import { useWishlist } from "../../context/wishlistContext";

const MyOrder = () => {
    const navigate = useNavigate();
    const { ipAddress } = useWishlist();

    const [orderData, setOrderData] = useState([]);
    const [getTracking, setGetTracking] = useState();
    const [orderNo, setOrderNo] = useState();
    const [activeButton, setActiveButton] = useState("success");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedOrderDetail, setSelectedOrderDetail] = useState(null);
    const [orderDetailLoading, setOrderDetailLoading] = useState(false);

    const itemsPerPage = 5;

    const [show, setShow] = useState(false);
    const handleClose = () => {
        setShow(false);
        setSelectedOrderDetail(null);
    };

    const [showTrackOrder, setShowTrackOrder] = useState(false);
    const handleCloseTrackOrder = () => setShowTrackOrder(false);
    const handleShowTrackOrder = () => setShowTrackOrder(true);

    const parseOrderProductDetails = (value) => {
        if (!value) return {};

        if (typeof value === "object") {
            return value;
        }

        try {
            return JSON.parse(value);
        } catch (error) {
            return {};
        }
    };

    const extractOrderDetails = (response) => {
        if (Array.isArray(response)) return response;
        if (Array.isArray(response?.data)) return response.data;
        return [];
    };

    const formatDate = (value) => {
        return value
            ? new Date(value).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
              })
            : "";
    };

    const getCurrencySymbol = useCallback((currencyCode = "", currencySymbol = "") => {
        if (currencySymbol && currencySymbol !== "$") return currencySymbol;
        if (currencyCode === "INR") return "\u20B9";
        if (currencyCode === "USD") return "$";
        return currencySymbol || "\u20B9";
    }, []);

    const formatPaymentMethod = (value = "") => {
        return String(value || "")
            .split("_")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    };

    const normalizeViewOrderDetail = (orderDetails = [], response = {}) => {
        const details = Array.isArray(orderDetails) ? orderDetails : [];
        const order = details?.[0]?.order || {};
        const shipping = order?.shipping_address_data || order?.billing_address_data || {};
        const itemSubtotal = details.reduce((total, detail) => {
            const qty = Number(detail?.qty || 1);
            const price = Number(detail?.price || 0);
            return total + price * qty;
        }, 0);
        const discount = details.reduce((total, detail) => {
            const qty = Number(detail?.qty || 1);
            return total + Number(detail?.discount || 0) * qty;
        }, 0);
        const couponDiscount = Number(order?.discount_amount || 0);
        const extraDiscount = Number(order?.extra_discount || 0);
        const shippingAmount = Number(
            order?.shipping_cost || order?.shipping_price || order?.deliveryman_charge || 0
        );
        const totalDiscount = discount + couponDiscount + extraDiscount;
        const grandTotal = Number(
            order?.order_amount || itemSubtotal + shippingAmount - totalDiscount
        );
        const currencyCode =
            response?.currency_code ||
            order?.currency_code ||
            details?.[0]?.product_details?.currency_code ||
            "";
        const currencySymbol = getCurrencySymbol(
            currencyCode,
            response?.currency_symbol || details?.[0]?.product_details?.currency_symbol || order?.currency_symbol
        );

        return {
            orderDate: order?.created_at,
            orderNo: order?.order_group_id || order?.id || details?.[0]?.order_id || "",
            shippingAddress: [
                shipping?.contact_person_name,
                shipping?.address,
                shipping?.city,
                shipping?.state,
                shipping?.country,
                shipping?.zip,
            ]
                .filter(Boolean)
                .join(", "),
            paymentMethod: formatPaymentMethod(order?.payment_method),
            currencySymbol,
            itemSubtotal,
            discount,
            couponDiscount,
            extraDiscount,
            shippingAmount,
            totalDiscount,
            totalAmount: grandTotal,
        };
    };

    const normalizeOrderListDetail = (item = {}) => {
        const orderItems = Array.isArray(item?.orderItemDtos) ? item.orderItemDtos : [];
        const itemSubtotal = orderItems.reduce((total, detail) => {
            const qty = Number(detail?.quantity || detail?.qty || 1);
            const price = Number(detail?.unitPrice || detail?.price || 0);
            return total + price * qty;
        }, 0);
        const discount = orderItems.reduce((total, detail) => {
            const qty = Number(detail?.quantity || detail?.qty || 1);
            return total + Number(detail?.discount || 0) * qty;
        }, 0);
        const couponDiscount = Number(item?.discount_amount || 0);
        const extraDiscount = Number(item?.extra_discount || 0);
        const shippingAmount = Number(item?.shipping_cost || item?.shipping_price || 0);
        const totalDiscount = discount + couponDiscount + extraDiscount;
        const grandTotal = Number(
            item?.totalAmount || itemSubtotal + shippingAmount - totalDiscount
        );

        return {
            ...item,
            paymentMethod: formatPaymentMethod(item?.payment_method),
            currencySymbol: item?.currencySymbol && item.currencySymbol !== "$" ? item.currencySymbol : "\u20B9",
            itemSubtotal: itemSubtotal || grandTotal + totalDiscount - shippingAmount,
            discount,
            couponDiscount,
            extraDiscount,
            shippingAmount,
            totalDiscount,
            totalAmount: grandTotal,
        };
    };

    const formatPdfAmount = (value) => Number(value || 0).toFixed(2);

    const getPdfCurrencySymbol = (currencyCode = "", currencySymbol = "") => {
        if (String(currencyCode || "").toUpperCase() === "INR") return "Rs.";
        return currencySymbol || getCurrencySymbol(currencyCode, currencySymbol);
    };

    const getAddressLines = (address = {}) => {
        return [
            address?.contact_person_name,
            address?.phone,
            address?.email,
            address?.address,
            [address?.city, address?.state].filter(Boolean).join(", "),
            [address?.country, address?.zip].filter(Boolean).join(" - "),
        ].filter(Boolean);
    };

    const getInvoiceProducts = (orderDetails = []) => {
        const products = new Map();

        orderDetails.forEach((detail) => {
            const product = parseOrderProductDetails(detail?.product_details);
            const productId = detail?.product_id || product?.id || detail?.id;
            const unitPrice = Number(detail?.price || 0);
            const qty = Number(detail?.qty || 1);
            const key = `${productId}-${unitPrice}`;
            const existing = products.get(key);

            if (existing) {
                existing.qty += qty;
                existing.tax += Number(detail?.tax || 0) * qty;
                existing.discount += Number(detail?.discount || 0) * qty;
                return;
            }

            products.set(key, {
                name: product?.name || detail?.product_all_status?.name || "Product",
                unitPrice,
                qty,
                tax: Number(detail?.tax || 0) * qty,
                discount: Number(detail?.discount || 0) * qty,
            });
        });

        return Array.from(products.values());
    };

    const downloadInvoicePdf = (orderDetails = [], response = {}) => {
        const details = Array.isArray(orderDetails) ? orderDetails : [];
        const order = details?.[0]?.order || {};
        const billing = order?.billing_address_data || {};
        const shipping = order?.shipping_address_data || billing;
        const currencyCode =
            response?.currency_code ||
            order?.currency_code ||
            details?.[0]?.product_details?.currency_code ||
            "";
        const currencySymbol = getPdfCurrencySymbol(
            currencyCode,
            response?.currency_symbol || details?.[0]?.product_details?.currency_symbol || order?.currency_symbol
        );
        const invoiceProducts = getInvoiceProducts(details);
        const subTotal = invoiceProducts.reduce(
            (total, product) => total + product.unitPrice * product.qty,
            0
        );
        const taxTotal = invoiceProducts.reduce((total, product) => total + product.tax, 0);
        const productDiscount = invoiceProducts.reduce(
            (total, product) => total + product.discount,
            0
        );
        const couponDiscount = Number(order?.discount_amount || 0);
        const shippingCost = Number(order?.shipping_cost || order?.shipping_price || 0);
        const grandTotal = Number(order?.order_amount || subTotal + taxTotal + shippingCost - productDiscount - couponDiscount);
        const orderDate = formatDate(order?.created_at || details?.[0]?.created_at);
        const orderId = details?.[0]?.order_id || order?.id || "";
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text("M.U. Gems & Jewellery", 14, 15);
        doc.setFontSize(20);
        doc.text("Order Invoice", 105, 25, { align: "center" });

        doc.setFontSize(12);
        doc.text(`Invoice: #${orderId}`, 14, 40);
        doc.text(`Order Date: ${orderDate}`, 150, 40);

        doc.setFontSize(12);
        doc.text("Shipping To:", 14, 55);
        doc.text(getAddressLines(shipping), 14, 62);

        doc.text("Billing Address:", 120, 55);
        doc.text(getAddressLines(billing), 120, 62);

        autoTable(doc, {
            startY: 100,
            head: [["SL", "Item", "Unit Price", "Qty", "Total"]],
            body: invoiceProducts.map((product, index) => [
                index + 1,
                product.name,
                `${currencySymbol} ${formatPdfAmount(product.unitPrice)}`,
                product.qty,
                `${currencySymbol} ${formatPdfAmount(product.unitPrice * product.qty)}`,
            ]),
        });

        const finalY = doc.lastAutoTable.finalY + 10;
        doc.text("Payment Details", 14, finalY);
        doc.text(formatPaymentMethod(order?.payment_method), 14, finalY + 8);
        doc.text(orderDate, 14, finalY + 16);

        autoTable(doc, {
            startY: finalY,
            margin: { left: 120 },
            body: [
                ["Item(s) Subtotal:", `${currencySymbol} ${formatPdfAmount(subTotal)}`],
                ["Tax:", `+ ${currencySymbol} ${formatPdfAmount(taxTotal)}`],
                ["Discount:", `- ${currencySymbol} ${formatPdfAmount(productDiscount)}`],
                ["Coupon Discount:", `- ${currencySymbol} ${formatPdfAmount(couponDiscount)}`],
                ["Shipping & Handling:", `+ ${currencySymbol} ${formatPdfAmount(shippingCost)}`],
                [
                    { content: "Grand Total", styles: { fontStyle: "bold" } },
                    {
                        content: `${currencySymbol} ${formatPdfAmount(grandTotal)}`,
                        styles: { fontStyle: "bold" },
                    },
                ],
            ],
            theme: "plain",
            styles: { halign: "right" },
        });

        doc.setFontSize(10);
        doc.text(
            "If you have any questions or feedback, please email us at life@lifecode.co.in",
            14,
            280
        );

        doc.save(`invoice-${orderId || "order"}.pdf`);
    };

    const GetProductOrderByOrderId = async (item) => {
        if (!item || !item.orderId) return;

        try {
            const response = await get(API_URL.orderDetailById, {
                order_id: item.orderId,
                ip_address: ipAddress,
            });
            const orderDetails = extractOrderDetails(response);

            if (orderDetails.length > 0) {
                downloadInvoicePdf(orderDetails, response);
            } else {
                toast.error(response?.message || "Unable to fetch invoice data.");
            }
        } catch (error) {
            toast.error("An error occurred. Please try again later.");
        }
    };

    const onViewOrderDetail = async (item) => {
        if (!item || !item.orderId) return;

        setOrderDetailLoading(true);
        setSelectedOrderDetail(null);
        setShow(true);

        try {
            const orderId = item.orderId;
            const response = await get(API_URL.orderDetailById, {
                order_id: orderId,
                ip_address: ipAddress,
            });
            const orderDetails = extractOrderDetails(response);

            if (orderDetails.length > 0) {
                setSelectedOrderDetail(normalizeViewOrderDetail(orderDetails, response));
            } else {
                setSelectedOrderDetail(normalizeOrderListDetail(item));
                toast.error(response?.message || "Unable to fetch order details.");
            }
        } catch (error) {
            setSelectedOrderDetail(normalizeOrderListDetail(item));
            toast.error("An error occurred. Please try again later.");
        } finally {
            setOrderDetailLoading(false);
        }
    };

    const mapOrderStatus = useCallback((status) => {
        switch ((status || "").toLowerCase()) {
            case "pending":
                return 1;
            case "confirmed":
                return 2;
            case "processing":
                return 3;
            case "failed":
                return 4;
            case "delivered":
                return 5;
            case "canceled":
            case "cancelled":
                return 6;
            default:
                return 1;
        }
    }, []);

    const normalizeOrders = useCallback((ordersArray) => {
        return (ordersArray || []).map((order) => {
            const billing = order?.billing_address_data || {};
            const details = order?.details || [];

            return {
                ...order,
                orderId: order?.id,
                orderNo: order?.order_group_id || order?.id,
                orderDate: order?.created_at,
                totalAmount: order?.order_amount || 0,
                currencySymbol: getCurrencySymbol(order?.currency_code, order?.currency_symbol),
                orderStatusId: mapOrderStatus(order?.order_status),
                cancelStatus:
                    order?.order_status === "canceled" || order?.order_status === "cancelled"
                        ? "Y"
                        : "N",
                name: billing?.contact_person_name || "User",
                shippingAddress:
                    billing?.address ||
                    order?.shipping_address_data?.address ||
                    order?.shipping_address ||
                    "",
                isShipStatus: order?.delivery_man_id ? "Y" : "N",

                orderItemDtos: details.map((detail) => {
                    const product = detail?.product || {};
                    const imagesFullUrl = product?.images_full_url || [];

                    const mappedImages = imagesFullUrl.map((img, index) => ({
                        imgPath: img?.path || "",
                        isHeader: index === 0,
                    }));

                    return {
                        ...detail,
                        productId: detail?.product_id || product?.id,
                        productName: product?.name || "Product",
                        productCategoryId: product?.category_id || "",
                        productSubCategoryId: product?.sub_category_id || "",
                        productSubCategoryName: product?.sub_category_id || "",
                        categoryName: product?.category_id || "",
                        quantity: detail?.qty || 1,
                        unitPrice: detail?.price || 0,
                        slug: product?.slug || "",
                        productImageDtos: mappedImages,
                        productSkuDtos: [],
                        productData: product,
                    };
                }),
            };
        });
    }, [getCurrencySymbol, mapOrderStatus]);

    const getNormalizedText = (value) => String(value || "").trim().toLowerCase();

    const isCancelledOrder = (item) => {
        const orderStatus = getNormalizedText(item?.order_status);
        return item?.cancelStatus === "Y" || orderStatus === "canceled" || orderStatus === "cancelled";
    };

    const isSuccessfulOrder = (item) => {
        const paymentMethod = getNormalizedText(item?.payment_method);
        const paymentStatus = getNormalizedText(item?.payment_status);

        const isRazorpaySuccess =
            paymentMethod === "razorpay" &&
            (paymentStatus === "success" || paymentStatus === "paid");

        const isPaypalSuccess =
            paymentMethod === "paypal" &&
            (paymentStatus === "success" || paymentStatus === "paid" || paymentStatus === "completed");

        const isCashOnDeliveryOrder =
            paymentMethod === "cash_on_delivery" && paymentStatus === "unpaid";

        return !isCancelledOrder(item) && (isRazorpaySuccess || isPaypalSuccess || isCashOnDeliveryOrder);
    };

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const response = await post(API_URL.Orderlists);
                const apiOrders = response?.orders || [];

                const normalized = normalizeOrders(apiOrders).sort(
                    (a, b) => new Date(b.orderDate) - new Date(a.orderDate)
                );

                setOrderData(normalized);
            } catch (error) {
                console.error("FETCH ORDERS ERROR =>", error);
                setOrderData([]);
            }
        };

        fetchOrders();
    }, [normalizeOrders]);

    const onTrackOrder = useCallback(
        async (item) => {
            handleShowTrackOrder();
            setGetTracking(null);
            setOrderNo(item?.orderNo);

            try {
                const response = await post(API_URL.trackOrder, {
                    phone_number:
                        item?.billing_address_data?.phone ||
                        item?.order?.billing_address_data?.phone ||
                        "",
                    order_id: String(item?.orderId || item?.id || ""),
                });

                setGetTracking(response?.data || response || null);
            } catch (error) {
                toast.error(error.response?.data?.message || "Please try again later.");
            }
        },
        []
    );

    const onProductDetail = (item) => {
        if (!item) return;

        localStorage.setItem("productId", item?.productId || "");
        localStorage.setItem("productCategoryId", item?.productCategoryId || "");
        localStorage.setItem("productSubCategoryId", item?.productSubCategoryId || "");

        if (item?.slug) {
            navigate(`/product/${item.slug}`);
            localStorage.setItem("priviousUrl", `/product/${item.slug}`);
        }
    };

    const handleButtonClick = (status) => {
        setActiveButton(status);
        setCurrentPage(1);
    };

    const filteredOrders = orderData.filter((item) => {
        if (activeButton === "success") {
            return isSuccessfulOrder(item);
        } else if (activeButton === "cancelled") {
            return isCancelledOrder(item);
        }
        return false;
    });

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const onCancelOrder = (id) => {
        navigate(`/cancel-order/${id}`);
    };

    return (
        <Layout showBreadcrumb={false}>
            <section className="product_wepper_main">
                <div className="container pt-sm-4 pt-2 pb-sm-5 pb-2">
                    <section className="contact-banner-sec">
                        <div className="container-fluid">
                            <div className="page-header">
                                <h1>YOUR ORDERS</h1>
                            </div>
                            <Breadcrumb currentLabel="Your Orders" />
                        </div>
                    </section>

                    <div className="filter_status_button mt-3">
                        <button
                            className={`status_filter ${activeButton === "success" ? "active" : ""}`}
                            onClick={() => handleButtonClick("success")}
                        >
                            Success
                        </button>
                        <button
                            className={`status_filter ${activeButton === "cancelled" ? "active" : ""}`}
                            onClick={() => handleButtonClick("cancelled")}
                        >
                            Cancelled Order
                        </button>
                    </div>

                    <div className="my_order_list mt-4">
                        {currentOrders?.length > 0 ? (
                            currentOrders?.map((item, i) => (
                                <div className="order_box mb-4" key={item?.orderId || item?.orderNo || i}>
                                    <div className="product_order_details d-md-flex justify-content-between">
                                        <div className="head_first_div d-sm-flex justify-content-sm-between">
                                            <div className="d-flex justify-content-between mb-sm-auto mb-3">
                                                <div className="order_id">
                                                    <p className="heading mb-0">Order placed</p>
                                                    <p className="description_detail mb-0">
                                                        {item?.orderDate
                                                            ? new Date(item?.orderDate).toLocaleDateString("en-GB", {
                                                                  day: "numeric",
                                                                  month: "long",
                                                                  year: "numeric",
                                                              })
                                                            : null}
                                                    </p>
                                                </div>
                                                <div className="total_order_amount">
                                                    <p className="total_title mb-0">Total</p>
                                                    <p className="total_amount mb-0">
                                                        {item?.currencySymbol || "\u20B9"}
                                                        {item?.totalAmount}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="order_id ship_to_address_hover">
                                                <p className="heading mb-0">Ship to</p>
                                                <div className="ship_to_button">
                                                    <button className="description_detail mb-0">
                                                        {item?.name} <FaChevronDown />
                                                    </button>
                                                    <div className="shipping_address arrow-top">
                                                        <div className="shipping_address_detail">
                                                            <p className="user_name mb-0">{item?.name}</p>
                                                            <p>{item?.shippingAddress}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="head_first_div mt-md-auto mt-3">
                                            <div
                                                className="order_id d-flex align-items-center justify-content-end"
                                                style={{ gap: "8px" }}
                                            >
                                                <p className="heading mb-0">orderNo :</p>
                                                <p className="description_detail mb-0">{item?.orderNo}</p>
                                            </div>

                                            <div className="d-flex align-items-center justify-content-end">
                                                <div className="view_detail_button">
                                                    <Button
                                                        type="button"
                                                        className="view_orderdetail_btn"
                                                        variant=""
                                                        onClick={() => onViewOrderDetail(item)}
                                                    >
                                                        View order details
                                                    </Button>

                                                    <Modal
                                                        show={show}
                                                        onHide={handleClose}
                                                        centered
                                                        dialogClassName="order-detail-modal"
                                                    >
                                                        <Modal.Header closeButton>
                                                            <Modal.Title>Order Details</Modal.Title>
                                                        </Modal.Header>
                                                        <Modal.Body>
                                                            {orderDetailLoading ? (
                                                                <p className="mb-0">Loading...</p>
                                                            ) : (
                                                                <ViewOrderDetail item={selectedOrderDetail || item} />
                                                            )}
                                                        </Modal.Body>
                                                    </Modal>
                                                </div>

                                                {!isCancelledOrder(item) && (
                                                    <>
                                                        <div className="between_sapration"></div>

                                                        <div className="invoice">
                                                            <button
                                                                title="Download Invoice"
                                                                className="invoice_button mb-0"
                                                                onClick={() => GetProductOrderByOrderId(item)}
                                                            >
                                                                Invoice <FaChevronDown />
                                                            </button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {item?.orderItemDtos?.length > 0 ? (
                                        <div className="product_order_data_wrapper d-sm-flex">
                                            <div className="col-sm-9 col-12">
                                                {item.orderItemDtos.map((element, j) => {
                                                    const imagePath =
                                                        element?.productImageDtos?.[0]?.imgPath ||
                                                        "./images/defaultimages.png";

                                                    return (
                                                        <div className="product_order_content row p-3" key={j}>
                                                            <div className="product_order__image col-xxl-3 col-xl-3 col-lg-3 col-md-4 col-sm-3 col-12">
                                                                <img
                                                                    onClick={() => onProductDetail(element)}
                                                                    style={{ cursor: "pointer" }}
                                                                    src={imagePath}
                                                                    className="w-100"
                                                                    alt="Product"
                                                                />
                                                            </div>

                                                            <div className="product_order_name col-xxl-9 col-xl-9 col-lg-9 col-md-8 col-sm-9 col-12">
                                                                <h5
                                                                    className="product_name"
                                                                    onClick={() => onProductDetail(element)}
                                                                    style={{ cursor: "pointer" }}
                                                                >
                                                                    {element?.productName}
                                                                </h5>
                                                                <div>
                                                                    <div className="d-flex" style={{ gap: "5px" }}>
                                                                        <p
                                                                            className="mb-1"
                                                                            style={{ color: "#ffffffb5" }}
                                                                        >
                                                                            Quantity :
                                                                        </p>
                                                                        <p className="mb-1">{element?.quantity}</p>
                                                                    </div>
                                                                    <p
                                                                        style={{
                                                                            color: "#fffffff0",
                                                                            fontWeight: "500",
                                                                        }}
                                                                    >
                                                                        ₹{element?.unitPrice}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <div className="order_btn_wrapper col-sm-3 col-12 pt-sm-3">
                                                {isCancelledOrder(item) ? (
                                                    <div className="bttn_wepper2">
                                                        <button
                                                            className="buy_it_order_btn cancelled_order_btn web_buttons"
                                                            disabled
                                                            type="button"
                                                        >
                                                            Cancelled Order
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <button
                                                            onClick={() => onTrackOrder(item)}
                                                            className="track_order_btn web_buttons"
                                                            title="click"
                                                            type="button"
                                                        >
                                                            Track Order
                                                        </button>

                                                        <Modal
                                                            show={showTrackOrder}
                                                            onHide={handleCloseTrackOrder}
                                                            centered
                                                        >
                                                            <Modal.Header closeButton>
                                                                <Modal.Title>Order Details</Modal.Title>
                                                            </Modal.Header>
                                                            <Modal.Body>
                                                                <TrackOrder
                                                                    trackOrder={getTracking}
                                                                    orderNo={orderNo}
                                                                />
                                                            </Modal.Body>
                                                        </Modal>
                                                    </>
                                                )}

                                                {!isCancelledOrder(item) && item?.cancelStatus === "N" && item.orderStatusId !== 4 && (
                                                    <div className="bttn_wepper2">
                                                        <button
                                                            className="buy_it_order_btn web_buttons"
                                                            onClick={() => onCancelOrder(item.orderId)}
                                                        >
                                                            Cancel Order
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            ))
                        ) : (
                            <div>
                                <p className="text-white">No orders available at the moment.</p>
                            </div>
                        )}
                    </div>

                    {currentOrders?.length > 0 ? (
                        <div className="pagination">
                            <div className="pagination_box">
                                {[...Array(totalPages).keys()].map((pageNumber) => (
                                    <button
                                        key={pageNumber}
                                        onClick={() => handlePageChange(pageNumber + 1)}
                                        className={currentPage === pageNumber + 1 ? "active" : ""}
                                    >
                                        {pageNumber + 1}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>
        </Layout>
    );
};

export default MyOrder;
